// Projectile art, drawn pointing right: white-hot core + coloured edge.
// Player shots read by weapon family; enemy shots are larger, bolder and outlined
// so they stay readable over busy combat (bright colours are reserved for threats).

import type Phaser from 'phaser';
import { COLORS, mix } from '../../config/palette';
import { TEX } from '../keys';
import { TAU, circlePath, clamp, newCanvas, rgba, roundRectPath, softDot, softEllipse } from './canvas';
import { buildRamp, falloff, gauss, paintRamp, teardrop, type RampStop } from './paint';

export function buildProjectileArt(scene: Phaser.Scene): void {
  pulse(scene);
  pellet(scene);
  rail(scene);
  rocket(scene);
  gravity(scene);
  enemyBolt(scene);
  enemyOrb(scene);
  enemyNeedle(scene);
  mortar(scene);
}

/** Energy ramp: transparent → colour → light colour → white. */
function energyRamp(edge: number, mid: number): RampStop[] {
  return [
    [0.04, edge, 0],
    [0.22, edge, 0.4],
    [0.45, edge, 0.9],
    [0.66, mid, 1],
    [0.84, mix(mid, 0xffffff, 0.6), 1],
    [0.95, 0xffffff, 1],
  ];
}

/** Threat ramp with an opaque dark rim so enemy shots read over bright effects. */
function threatRamp(rim: number, edge: number, mid: number): RampStop[] {
  return [
    [0.02, rim, 0],
    [0.1, rim, 0.92],
    [0.26, edge, 1],
    [0.52, mid, 1],
    [0.76, mix(mid, 0xffffff, 0.55), 1],
    [0.9, 0xffffff, 1],
  ];
}

function pulse(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.projPulse, 36, 14);
  if (!c) return;
  const lut = buildRamp(energyRamp(COLORS.volt, 0x9ff6ff));
  paintRamp(c.g, 0, 0, 36, 14, (x, y) => {
    const core = teardrop(x, y, 29, 7, 4.4, 1, 1.4, 0.75);
    const glow = gauss(y - 7, 2.6) * clamp((x - 2) / 30, 0, 1) * falloff(x, 30, 35) * 0.42;
    return clamp(Math.max(core * (0.55 + 0.45 * clamp((x - 6) / 22, 0, 1)), glow), 0, 1);
  }, lut);
  c.tex.refresh();
}

function pellet(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.projPellet, 16, 10);
  if (!c) return;
  const lut = buildRamp([
    [0.04, 0xff9a1f, 0],
    [0.25, 0xffa020, 0.55],
    [0.5, COLORS.explosive, 1],
    [0.74, 0xfff0b8, 1],
    [0.9, 0xffffff, 1],
  ]);
  paintRamp(c.g, 0, 0, 16, 10, (x, y) => {
    const core = teardrop(x, y, 11, 5, 3.3, 1, 1.1, 0.6);
    const glow = gauss(Math.hypot(x - 10, y - 5), 3.6) * 0.38;
    return clamp(Math.max(core, glow), 0, 1);
  }, lut);
  c.tex.refresh();
}

function rail(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.projRail, 96, 12);
  if (!c) return;
  const lut = buildRamp(energyRamp(COLORS.volt, 0xc8fbff));
  paintRamp(c.g, 0, 0, 96, 12, (x, y) => {
    const core = teardrop(x, y, 89, 6, 3.8, 1, 1.2, 0.45);
    const glow = gauss(y - 6, 2.4) * clamp((x - 1) / 80, 0, 1) * falloff(x, 88, 95) * 0.45;
    return clamp(Math.max(core * (0.6 + 0.4 * clamp(x / 80, 0, 1)), glow), 0, 1);
  }, lut);
  c.tex.refresh();
}

function rocket(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.projRocket, 36, 14);
  if (!c) return;
  const g = c.g;
  // Exhaust flame (points left, behind the body).
  const flameLut = buildRamp([
    [0.04, 0xff5a10, 0],
    [0.25, 0xff6a1a, 0.6],
    [0.5, COLORS.missile, 1],
    [0.75, 0xffe08a, 1],
    [0.92, 0xffffff, 1],
  ]);
  paintRamp(g, 0, 0, 36, 14, (x, y) => {
    // Round, hottest end at the nozzle (x = 12); the plume tapers off to the left.
    const t = teardrop(x, y, 12, 7, 3.2, 0, 1.1, 0.8);
    return x < 16 ? t * (0.55 + 0.45 * clamp(x / 12, 0, 1)) : 0;
  }, flameLut);
  softEllipse(g, 9, 7, 9, 4.5, 0xff8a2a, 0.35);
  // Fins.
  g.fillStyle = rgba(0x3a414c);
  g.beginPath();
  g.moveTo(12, 3.4);
  g.lineTo(15, 0.6);
  g.lineTo(18, 0.6);
  g.lineTo(17, 3.6);
  g.closePath();
  g.fill();
  g.beginPath();
  g.moveTo(12, 10.6);
  g.lineTo(15, 13.4);
  g.lineTo(18, 13.4);
  g.lineTo(17, 10.4);
  g.closePath();
  g.fill();
  // Body.
  roundRectPath(g, 12, 3.6, 17, 6.8, 2.2);
  const body = g.createLinearGradient(0, 3.6, 0, 10.4);
  body.addColorStop(0, rgba(0xc9d3df));
  body.addColorStop(0.35, rgba(0x8592a3));
  body.addColorStop(1, rgba(0x3a414c));
  g.fillStyle = body;
  g.fill();
  // Warhead band + nose cone.
  g.fillStyle = rgba(COLORS.missile);
  g.fillRect(25, 3.8, 2.2, 6.4);
  g.beginPath();
  g.moveTo(28.5, 3.7);
  g.quadraticCurveTo(35.5, 7, 28.5, 10.3);
  g.closePath();
  const nose = g.createLinearGradient(28, 0, 35, 0);
  nose.addColorStop(0, rgba(0xdfe6ee));
  nose.addColorStop(1, rgba(0xffffff));
  g.fillStyle = nose;
  g.fill();
  g.fillStyle = 'rgba(255,255,255,0.8)';
  g.fillRect(14, 4.6, 10, 1);
  c.tex.refresh();
}

function gravity(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.projGravity, 32, 32);
  if (!c) return;
  const g = c.g;
  softDot(g, 16, 16, 16, COLORS.gravity, 0.55);
  // Accretion ring (ramp field) around a dark singularity core.
  const lut = buildRamp(energyRamp(COLORS.gravity, 0xc9a8ff));
  paintRamp(g, 0, 0, 32, 32, (x, y) => {
    const r = Math.hypot(x - 16, y - 16);
    const ringV = gauss(r - 9.2, 1.6) + 0.35 * gauss(r - 9.2, 3.2);
    const swirl = 0.85 + 0.15 * Math.sin(Math.atan2(y - 16, x - 16) * 3 + r * 0.5);
    return clamp(ringV * swirl, 0, 1) * falloff(r, 13, 16);
  }, lut);
  circlePath(g, 16, 16, 6.4);
  const core = g.createRadialGradient(15, 15, 0, 16, 16, 6.6);
  core.addColorStop(0, rgba(0x05020c));
  core.addColorStop(0.75, rgba(0x150a2a));
  core.addColorStop(1, rgba(0x6a3fd0));
  g.fillStyle = core;
  g.fill();
  g.fillStyle = 'rgba(255,255,255,0.9)';
  g.beginPath();
  g.arc(19.5, 11.2, 1.3, 0, TAU);
  g.fill();
  c.tex.refresh();
}

function enemyBolt(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.projEnemyBolt, 44, 20);
  if (!c) return;
  softEllipse(c.g, 24, 10, 21, 9.5, COLORS.threat, 0.4);
  const lut = buildRamp(threatRamp(0x4a0612, COLORS.threat, COLORS.threatHot));
  paintRamp(c.g, 0, 0, 44, 20, (x, y) => {
    const v = teardrop(x, y, 34, 10, 6.6, 3, 1.3, 0.7);
    return v * (0.55 + 0.45 * clamp((x - 6) / 28, 0, 1));
  }, lut);
  c.tex.refresh();
}

function enemyOrb(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.projEnemyOrb, 34, 34);
  if (!c) return;
  softDot(c.g, 17, 17, 17, COLORS.threatHot, 0.5);
  const lut = buildRamp(threatRamp(0x4a0a08, COLORS.threat, COLORS.threatHot));
  paintRamp(c.g, 0, 0, 34, 34, (x, y) => {
    const r = Math.hypot(x - 17, y - 17);
    const core = falloff(r, 0, 9.5) ** 0.55;
    const ringV = gauss(r - 12.2, 0.9) * 0.62 * (0.75 + 0.25 * Math.cos(Math.atan2(y - 17, x - 17) * 4));
    return clamp(Math.max(core, ringV), 0, 1);
  }, lut);
  c.tex.refresh();
}

function enemyNeedle(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.projEnemyNeedle, 48, 12);
  if (!c) return;
  softEllipse(c.g, 28, 6, 20, 5.2, COLORS.threat, 0.38);
  const lut = buildRamp(threatRamp(0x4a0618, COLORS.threat, 0xff9db0));
  paintRamp(c.g, 0, 0, 48, 12, (x, y) => {
    // Dart: widest at 72% of the length, razor tip at x = 46.
    const u = clamp((x - 2) / 44, 0, 1);
    const hw = u < 0.72 ? 3.6 * Math.pow(u / 0.72, 0.6) : 3.6 * Math.pow((1 - u) / 0.28, 0.85);
    return falloff(Math.abs(y - 6), hw - 1.1, hw + 0.6) * (0.5 + 0.5 * u);
  }, lut);
  c.tex.refresh();
}

function mortar(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.projMortar, 30, 30);
  if (!c) return;
  const g = c.g;
  softDot(g, 15, 15, 15, COLORS.threatHot, 0.9, 0.2);
  // Hot rim around the shell keeps the silhouette readable on dark backgrounds.
  circlePath(g, 15, 15, 9.8);
  g.fillStyle = rgba(0xffb070, 0.95);
  g.fill();
  // Dark shell.
  circlePath(g, 15, 15, 8.4);
  const shell = g.createRadialGradient(12, 11.5, 1, 15, 15, 9);
  shell.addColorStop(0, rgba(0x7a6e66));
  shell.addColorStop(0.55, rgba(0x3a322e));
  shell.addColorStop(1, rgba(0x1a1412));
  g.fillStyle = shell;
  g.fill();
  // Glowing heat band.
  g.save();
  circlePath(g, 15, 15, 8.4);
  g.clip();
  g.translate(15, 15);
  g.rotate(-0.5);
  g.fillStyle = rgba(COLORS.threatHot);
  g.fillRect(-10, -2.2, 20, 4.4);
  g.fillStyle = rgba(0xffe0a8);
  g.fillRect(-10, -0.8, 20, 1.6);
  g.restore();
  // White-hot fuse cap at the front.
  softDot(g, 23, 15, 5, 0xffe2b0, 1, 0.3);
  softDot(g, 23, 15, 2.4, 0xffffff, 1, 0.5);
  c.tex.refresh();
}
